/** @jsxImportSource @emotion/react */
import { useState } from 'react';

import { useFeatureFlags } from '@kaiizen/kai-pro-api-react-state';

import { useToast } from '@/Client/State';
import { useDispatchAction } from '@/Client/State/useDispatchAction';
import { DispatchModal } from '@/Client/Widgets/DispatchModal';
import { MAIN_NAVIGATION_BUTTON_STYLES, type MainNavigationButtonStyles } from './mainNavigationStyles';

export const BUTTON_TEXT = 'Dispatch';

export const DispatchHeaderAction = ({ buttonStyles = MAIN_NAVIGATION_BUTTON_STYLES }: { buttonStyles?: MainNavigationButtonStyles }) => {
  const { featureFlags } = useFeatureFlags();
  const { scanHasScopedTasks, agentOptions, dispatchedUserIds, isDispatched, projectAddress, projectLabel, applyDispatch } = useDispatchAction();
  const { showErrorToast } = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Once dispatched, this control disappears entirely - the assignment is
  // surfaced elsewhere in the product, not as a status/agent name here.
  if (featureFlags.turnBasedHandoff || isDispatched) return null;

  const buttonStyle = scanHasScopedTasks ? buttonStyles.secondary : buttonStyles.primary;

  const confirmDispatch = async (selectedAgentIds: string[]) => {
    setSubmitting(true);
    try {
      await applyDispatch(selectedAgentIds);
      setModalOpen(false);
    } catch (error) {
      // Leave the modal open on failure so the admin can retry - Promise.all
      // inside applyDispatch doesn't roll back whatever partially succeeded,
      // but re-applying an already-applied assignment is a harmless no-op.
      showErrorToast(error, 'dispatch this scope');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button type={'button'} css={buttonStyle} onClick={() => setModalOpen(true)}>
        {BUTTON_TEXT}
      </button>
      {modalOpen
        ? (
          <DispatchModal
            agentOptions={agentOptions}
            defaultAgentIds={dispatchedUserIds}
            projectAddress={projectAddress}
            projectLabel={projectLabel}
            busy={submitting}
            onConfirm={confirmDispatch}
            onCancel={() => setModalOpen(false)}
          />
        )
        : null}
    </>
  );
};
